import { define } from 'wicked-elements'
import arrayShuffle from 'array-shuffle'
import apostrophes from 'typographic-apostrophes'
import quotes from 'typographic-quotes'
const COLLECTION_ENDPOINT = '/wp-json/cmoa-api/v1/fetch-collection-data'
const createNode = (html) =>
  new DOMParser().parseFromString(html, 'text/html').body.firstChild
function irnPath(path) {
  return path.toString().padStart(4, '0')
}
function baseName(path, join = true) {
  const splitPath = path
    .split(/[\\/]/)
    .pop()
    .split(/\.(\w+)$/)

  if (join) {
    return splitPath.join()
  }
  return splitPath
}
function formatQuotes(str) {
  return str ? quotes(apostrophes(str), { locale: "en-us" }) : "";
}

define('[data-component~="collection-items"]', {
  init() {
    try {
      const collectionData = JSON.parse(this.element.dataset.collectionIds)
      this.collectionIds = collectionData.map(v => v.id)
      this.collectionAlts = collectionData.reduce((a, v) => {
        a[v.id] = v.alt
        return a
      }, {})
    } catch (e) {
      this.collectionIds = []
    }

    // Render empty if no collection ids were supplied
    if (!this.collectionIds.length) {
      this.renderEmpty()
      return
    }

    // Randomize if necessary
    this.randomize = this.element.dataset.collectionRandomize === '1'
    if (this.randomize) {
      this.collectionIds = arrayShuffle(this.collectionIds)
    }

    // Set base url
    this.collectionUrl = this.element.dataset.collectionUrl

    // Determine number of items based on columns/rows, or fill
    let subset = []
    if (this.element.dataset.collectionRows === 'fill') {
      subset = this.collectionIds
    } else {
      let columns = parseInt(this.element.dataset.collectionColumns) || 5
      let rows = parseInt(this.element.dataset.collectionRows) || 1
      let count = columns * rows
      subset = this.collectionIds.slice(0, count)
    }

    // Format ids
    subset = subset.map((id) => {
      if (!id.startsWith('cmoa:objects/')) {
        return `cmoa:objects/${id}`
      } else {
        return id
      }
    })

    // Fetch objects from the collection
    this.fetchObjectsFromCollectionApi(subset)
      .then((docs) => {
        this.objects = docs
        this.renderObjects()
      })
      .catch(() => {
        this.renderError()
      })
  },
  renderObjects() {
    this.element.innerHTML = ''
    this.objects.forEach((object) => {
      const title = object._source?.title
      const creator = object._source?.creators?.[0]?.name
      const year = object._source?.creation_date
        .reduce((prev, current) =>
          !prev || prev === current ? current : `${prev} - ${current}`
        )
        .replace('-', '–')
      const image = object._source?.images.find((image) => image.permitted)
      const url = this.collectionUrl + '/' + object._id.replace('cmoa:objects', 'objects')
      const id = object._id.replace('cmoa:objects/', '')
      const alt = this.collectionAlts[id] ? `alt="${this.collectionAlts[id]}"` : ''

      let src = false
      let ratio = false
      if (image && image.irn && image.filename) {
        const [filename] = baseName(image?.filename, false)
        const thumbnailFilename = `${filename}-840.jpg`
        src = encodeURI(
          `https://cmoa-collection-images.s3.amazonaws.com/${
            object._source?.type
          }/${irnPath(image?.irn)}/sizes/${thumbnailFilename}`
        )
        ratio = `aspect-ratio:${image.width}/${image.height};`
      }

      this.element.appendChild(
        createNode(`
        <div class="flex-1">
          <div data-sublink>
            <figure>
              ${
                src
                  ? `<img src="${src}" style="${ratio}" ${alt} />`
                  : `<div class="aspect-4/3 border border-black"></div>`
              }
            </figure>
            <div class="pt-lh-0.4">
              <a class="font-bold" href="${url}">${formatQuotes(creator)}</a>
              <div>${formatQuotes(title)}</div>
            </div>
            <div class="pt-lh-0.25 text-sm metadata">${year}</div>
          </dev>
        </div>
      `)
      )
    })
  },
  renderEmpty() {
    this.element.innerHTML = 'No collection objects found.'
  },
  renderError() {
    this.element.innerHTML =
      'There was an error fetching objects from the collection.'
  },
  async fetchObjectsFromCollectionApi(ids) {
    const response = await fetch(COLLECTION_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ object_ids: ids }),
    })

    if (!response.ok) {
      throw new Error(`Error fetching data: ${response.statusText}`)
    }

    const data = await response.json()
    return data
  },
})
